import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import {
    DailyTaskApi,
    DailyTask,
    DailyTaskRespondRequest,
    DailyTaskValidateRequest,
    DailyTaskResponse,
    DailyTaskValidateResponse
} from "@features/shared/api/course-data.api";
import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { trackUniversalEvent } from "../../shared/events";

// SECTION 1: FEATURE'S EVENTS
export const dailyTaskClicked = createEvent<string>();
export const dailyTaskStarted = createEvent<string>();
export const dailyTaskPromptSent = createEvent<{ task_id: string; prompt: string }>();
export const dailyTaskCompleted = createEvent<string>();
export const dailyTaskFinalScreenCourseClicked = createEvent<number>();
export const addUserMessageToTaskResponseHistory = createEvent<string>();
export const resetDailyTaskSession = createEvent();
export const dailyTaskTryAgain = createEvent();
export const dailyTaskRefetchRequested = createEvent<void>();

// SECTION 2: FEATURE'S STORES
export interface DailyTaskResponseHistory {
    message: string;
    side: "user" | "assistant";
    timestamp: number;
}

export const $dailyTask = createStore<DailyTask | null>(null);
export const $dailyTaskResponseHistory = createStore<DailyTaskResponseHistory[]>([]);
export const $dailyTaskValidateResponse = createStore<DailyTaskValidateResponse | null>(null);
export const $dailyTaskRespondLoading = createStore<boolean>(false);
export const $dailyTaskValidateLoading = createStore<boolean>(false);
export const $dailyTaskCompleted = createStore<boolean>(false);
export const $onboardingShown = createStore<boolean>(false);

// SECTION 3: FEATURE'S EFFECTS
export const getDailyTaskFx = createEffect<void, DailyTask | null>(async () => {
    const response = await DailyTaskApi.getDailyTask();
    return response;
});

export const dailyTaskRespondFx = createEffect<DailyTaskRespondRequest, DailyTaskResponse>(async (data) => {
    const response = await DailyTaskApi.respondToDailyTask(data);
    return response;
});

export const dailyTaskValidateFx = createEffect<DailyTaskValidateRequest, DailyTaskValidateResponse>(async (data) => {
    const response = await DailyTaskApi.validateDailyTask(data);
    return response;
});

export const markDailyTaskCompletedFx = createEffect<number, void>(async (taskId) => {
    // API call to mark task as completed will be handled by backend when validation passes
});

// SECTION 4: FEATURE'S SAMPLES, INNER RELATIONS
// Store updates
$dailyTask.on(getDailyTaskFx.doneData, (_, task) => task);

$dailyTaskResponseHistory
    .on(addUserMessageToTaskResponseHistory, (history, message) => [
        ...history,
        {
            side: "user" as const,
            message: message,
            timestamp: new Date().getTime()
        }
    ])
    .on(dailyTaskRespondFx.doneData, (history, { response }) => [
        ...history,
        {
            side: "assistant" as const,
            message: response,
            timestamp: new Date().getTime()
        }
    ])
    .reset([resetDailyTaskSession, dailyTaskTryAgain]);

$dailyTaskValidateResponse.on(dailyTaskValidateFx.doneData, (_, response) => response).reset([resetDailyTaskSession, dailyTaskTryAgain]);

$dailyTaskRespondLoading
    .on(dailyTaskRespondFx, () => true)
    .on(dailyTaskRespondFx.done, () => false)
    .on(dailyTaskRespondFx.fail, () => false);

$dailyTaskValidateLoading
    .on(dailyTaskValidateFx, () => true)
    .on(dailyTaskValidateFx.done, () => false)
    .on(dailyTaskValidateFx.fail, () => false);

$dailyTaskCompleted.on(dailyTaskCompleted, () => true).reset([resetDailyTaskSession, dailyTaskTryAgain]);

$onboardingShown.on(dailyTaskStarted, () => true);

// Analytics tracking
sample({
    clock: dailyTaskClicked,
    fn: (task_id) => ({ task_id }),
    target: createEffect((params: { task_id: string }) => {
        trackUniversalEvent("daily_task_clicked", params);
    })
});

sample({
    clock: dailyTaskStarted,
    fn: (task_id) => ({ task_id }),
    target: createEffect((params: { task_id: string }) => {
        trackUniversalEvent("daily_task_started", params);
    })
});

sample({
    clock: dailyTaskPromptSent,
    fn: ({ task_id, prompt }) => ({ task_id, prompt }),
    target: createEffect((params: { task_id: string; prompt: string }) => {
        trackUniversalEvent("daily_task_prompt_sent", params);
    })
});

const trackValidationResultFx = createEffect(
    ({ task, allCompleted }: { task: DailyTask | null; allCompleted: boolean }) => {
        if (task && allCompleted) {
            dailyTaskCompleted(task.id);
        }
    }
);

sample({
    clock: dailyTaskValidateFx.doneData,
    source: $dailyTask,
    filter: (task) => task !== null,
    fn: (task, response) => {
        const allCompleted = response.every((item) => item.completed);
        const user_score = response.filter((item) => item.completed).length;
        const passing_score = response.length;

        trackUniversalEvent("daily_task_prompt_send_result", {
            task_id: task!.id,
            user_score,
            passing_score
        });

        return { task, allCompleted };
    },
    target: trackValidationResultFx
});

sample({
    clock: dailyTaskValidateFx.doneData,
    filter: (r) => {
        const allCompleted = r.every((item) => item.completed);
        return allCompleted;
    },
    target: getDailyTaskFx
});

sample({
    clock: dailyTaskCompleted,
    fn: (task_id) => ({ task_id }),
    target: createEffect((params: { task_id: string }) => {
        trackUniversalEvent("daily_task_completed", params);
    })
});

sample({
    clock: dailyTaskFinalScreenCourseClicked,
    fn: (course_id) => ({ course_id }),
    target: createEffect((params: { course_id: number }) => {
        trackUniversalEvent("daily_task_final_screen_course_clicked", params);
    })
});

// Error handling
sample({
    clock: [getDailyTaskFx.fail, dailyTaskRespondFx.fail, dailyTaskValidateFx.fail],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

sample({
    clock: dailyTaskRefetchRequested,
    target: getDailyTaskFx
});

// SECTION 5: FEATURE'S CONTRACT
const contract = new ModelContract(
    {
        $dailyTask,
        $dailyTaskResponseHistory,
        $dailyTaskValidateResponse,
        $dailyTaskRespondLoading,
        $dailyTaskValidateLoading,
        $dailyTaskCompleted,
        $onboardingShown
    },
    {
        dailyTaskClicked,
        dailyTaskStarted,
        dailyTaskPromptSent,
        dailyTaskCompleted,
        dailyTaskFinalScreenCourseClicked,
        addUserMessageToTaskResponseHistory,
        resetDailyTaskSession,
        dailyTaskTryAgain,
        dailyTaskRefetchRequested
    },
    {
        dailyTaskRespondFx,
        dailyTaskValidateFx,
        markDailyTaskCompletedFx
    },
    {}
);

export type IDailyTaskContract = typeof contract;

export class DailyTaskModule extends ModuleDescription<IDailyTaskContract> {
    constructor(public userAuthModule: IUserAuthContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        // Reset state when user logs out
        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [
                $dailyTask,
                $dailyTaskResponseHistory,
                $dailyTaskValidateResponse,
                $dailyTaskValidateLoading,
                $dailyTaskRespondLoading,
                $dailyTaskCompleted,
                $onboardingShown
            ]
        });

        // Auto-load daily task when user is authenticated
        sample({
            clock: this.userAuthModule.events.authenticated,
            target: getDailyTaskFx
        });
    }
}
